import './styles.scss';

import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import useHover from '@assets/scripts/hooks/useHover';

import Input from '@components/atoms/Input';
import ButtonPrimary from '@components/atoms/buttons/ButtonPrimary';

import { PageContext } from '@assets/scripts/context/page-context';
import forms from '@data/forms';

const Form = ({
	modifiers,
	className,
	fields,
	onFormSubmit,
	submit,
	children,
	...otherProps
}) => {
	const [inputs, setInputs] = useState(() =>
		fields.reduce((acc, field) => ({ ...acc, [field.id]: '' }), {})
	);
	const [serverState, setServerState] = useState({
		submitting: false,
		status: null,
	});
	const [errors, setErrors] = useState({});

	const [isFocused, setIsFocused] = useState(false);

	const baseClass = 'form';
	const rootClass = cx(
		baseClass,
		className,
		{ 'is-focused': isFocused },
		{
			[`${baseClass}--${modifiers}`]: modifiers,
		}
	);

	const { currentLanguage } = useContext(PageContext);

	// Specify for a field `validation: 'required'`
	const validationRules = {
		required: (val) => !!val,
		email: (val) => val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
	};

	/* eslint react-hooks/exhaustive-deps: off */
	useEffect(() => {
		// Only perform interactive validation after submit
		if (Object.keys(errors).length > 0) {
			validate();
		}
	}, [inputs]);
	/* eslint react-hooks/exhaustive-deps: off */

	const handleInputsChange = (event) => {
		event.persist();
		setInputs((prev) => ({
			...prev,
			[event.target.id]:
				event.target.type === 'checkbox'
					? event.target.checked
					: event.target.value,
		}));
	};

	const handleFormSubmit = (event) => {
		event.preventDefault();

		if (!validate() || serverState.submitting) return false;

		setServerState({ submitting: true });

		onFormSubmit(inputs, handleServerResponse);
	};

	const validate = () => {
		let fieldErrors = {};
		let hasErrors = false;

		fields.forEach((field) => {
			const { id, validation } = field;
			if (validation) {
				fieldErrors[id] = !validationRules[validation](inputs[id]);
				hasErrors |= fieldErrors[id];
			}
		});

		setErrors((prev) => ({ ...prev, ...fieldErrors }));
		return !hasErrors;
	};

	const getErrorMessage = (field) => {
		if (errors[field]) {
			switch (field) {
				case 'email':
					return forms[currentLanguage].errors.email;
				case 'gdpr':
					return forms[currentLanguage].errors.gdpr;
				default:
					return `${forms[currentLanguage].errors.default} ${
						fields.find((f) => f?.id === field)?.label ?? field
					}`;
			}
		}
	};

	const handleServerResponse = (success, msg) => {
		setServerState({
			submitting: false,
			status: { success, msg },
		});

		if (success) {
			setErrors({});
			setInputs(
				() =>
					fields.reduce(
						(acc, field) => ({ ...acc, [field.id]: '' }),
						{}
					) // Clear inputs
			);
		}
	};

	const handleFirstFieldFocus = () => {
		if (!isFocused) setIsFocused(true);
	};
	const handleFirstFieldBlur = (event) => {
		// if (!event.target.value) setIsFocused(false);
	};

	// interaction
	const [hoverOnInput, hoverOffInput] = useHover({ cursorScale: 0 });

	return (
		<form
			className={rootClass}
			onSubmit={handleFormSubmit}
			noValidate
			{...otherProps}
		>
			{!serverState.status?.success && (
				<>
					<div className="form__fields">
						{fields.map((field, index) => (
							<Input
								key={index}
								{...field}
								value={inputs[field.id]}
								onInputChange={handleInputsChange}
								error={getErrorMessage(field.id)}
								onFocus={
									index === 0 ? handleFirstFieldFocus : null
								}
								onBlur={
									index === 0 ? handleFirstFieldBlur : null
								}
								onMouseEnter={() => hoverOnInput()}
								onMouseLeave={() => hoverOffInput()}
								data-target
							/>
						))}
					</div>

					<ButtonPrimary
						text={submit}
						data-target
						className="form__submit"
						disabled={serverState.submitting}
						type="submit"
					/>

					{serverState.status && (
						<p className="form__error">{serverState.status.msg}</p>
					)}
				</>
			)}
			{serverState.status?.success && (
				<p className="form__success">{serverState.status.msg}</p>
			)}
		</form>
	);
};

Form.defaultProps = {
	submit: 'Send',
};

Form.propTypes = {
	modifiers: PropTypes.string,
	className: PropTypes.string,
	fields: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)).isRequired,
	onFormSubmit: PropTypes.func.isRequired,
	submit: PropTypes.string,
};

export default Form;
